import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/* ===== API base (inline, per your pattern) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

const MAX_IMAGES = 5;
const imgSrc = (fileName) => `${API_BASE}/uploads/blogs/${fileName}`;

/* Minimal HTTP helper for JSON-less (list/delete) calls */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

/* multipart helper for create/update (images are files) */
async function httpForm(path, { method = "POST", formData } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    body: formData,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

const api = {
  list: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/blogs${qs}`);
  },
  create: (formData) => httpForm(`/api/blogs`, { method: "POST", formData }),
  update: (id, formData) => httpForm(`/api/blogs/${id}`, { method: "PUT", formData }),
  remove: (id) => http(`/api/blogs/${id}`, { method: "DELETE" }),
};

const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  accent: "#ff914d",
  text: "#f1f1f1",
  subtext: "#d6d9de",
  bg: "#0b1320",
  card: "#0f1b2d",
  border: "#1e293b",
};

const Page = styled.div`display: grid; gap: 16px; color: ${theme.text};`;
const Title = styled.h2`margin: 0; font-weight: 800;`;
const Row = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;
`;
const Button = styled.button`
  background: ${theme.brand}; color: #fff; border: 0; padding: 10px 14px; border-radius: 10px; font-weight: 700; cursor: pointer;
  &:hover{ background: ${theme.brandDark}; }
  &:disabled{ opacity: .6; cursor: not-allowed; }
`;
const Card = styled.div`
  background: ${theme.card}; border: 1px solid ${theme.border}; border-radius: 14px; overflow: hidden;
`;
const Scroll = styled.div`max-width: 100%; overflow: auto;`;
const Table = styled.table`
  width: 100%; min-width: 900px; border-collapse: collapse; color: ${theme.text};
  th, td { border-bottom: 1px solid ${theme.border}; padding: 12px; text-align: left; vertical-align: top; }
  th { background: rgba(255,255,255,0.03); white-space: nowrap; }
`;
const Badge = styled.span`
  padding: 2px 8px; border-radius: 999px; font-size: 12px; border: 1px solid ${theme.border};
  background: ${p=>p.$status==='published' ? 'rgba(46,204,113,.15)' : 'rgba(255,145,77,.15)'};
`;

const ModalWrap = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 24px;
  overflow: auto;
  overscroll-behavior: contain;
`;
const ModalCard = styled.div`
  width: min(820px, 92vw);
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const ModalBody = styled.div`
  display: grid;
  gap: 14px;
  overflow: auto;
  padding: 16px;
  padding-right: 18px;
`;
const Field = styled.label`display: grid; gap: 6px; font-size: 14px;`;
const Input = styled.input`
  padding: 10px 12px; border-radius: 10px; border: 1px solid ${theme.border}; background: #0c1626; color: ${theme.text};
`;
const Textarea = styled.textarea`
  min-height: 160px; padding: 12px; border-radius: 12px; border: 1px solid ${theme.border}; background: #0c1626; color: ${theme.text}; resize: vertical;
`;
const Actions = styled.div`display: flex; gap: 10px; justify-content: flex-end;`;
const ContentCell = styled.span`
  display: inline-block; max-width: 380px;
  overflow: hidden; white-space: nowrap; text-overflow: ellipsis;
  cursor: pointer; color: ${theme.subtext};
  &:hover { text-decoration: underline; color: #ffffff; }
`;
const Hr = styled.hr`border:0; border-top:1px solid ${theme.border}; margin: 12px 0;`;

/* ---- image picker ---- */
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
`;
const ImageTile = styled.div`
  position: relative;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 1;
  background: #0c1626;
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;
const RemoveBtn = styled.button`
  position: absolute;
  top: 4px; right: 4px;
  width: 24px; height: 24px;
  border-radius: 50%;
  border: 0;
  background: rgba(0,0,0,.65);
  color: #fff;
  cursor: pointer;
  line-height: 1;
  font-size: 14px;
`;
const AddTile = styled.label`
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  border: 1px dashed ${theme.border};
  border-radius: 10px;
  color: ${theme.subtext};
  cursor: pointer;
  font-size: 24px;
  &:hover { border-color: ${theme.brand}; color: ${theme.brand}; }
  input { display: none; }
`;
const HelperText = styled.small`color: ${theme.subtext};`;

/* ---- links editor ---- */
const LinkRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr auto;
  gap: 8px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const IconBtn = styled.button`
  background: #b91c1c; color: #fff; border: 0; border-radius: 8px; padding: 0 12px; cursor: pointer; font-weight: 700;
`;
const GhostBtn = styled.button`
  background: transparent; color: ${theme.brand}; border: 1px dashed ${theme.border}; border-radius: 10px; padding: 8px 12px; cursor: pointer; font-weight: 700; text-align: left;
  &:hover { border-color: ${theme.brand}; }
`;

const DEFAULT_AUTHOR = "Abhishek Shete";
const emptyForm = { title: "", content: "", status: "published", author: DEFAULT_AUTHOR };

export default function BlogManager() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [existingImages, setExistingImages] = useState([]); // [{fileName, originalName}] kept from server
  const [newImages, setNewImages] = useState([]); // [{file, previewUrl}]
  const [links, setLinks] = useState([]); // [{label, url}]
  const [error, setError] = useState("");

  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const res = await api.list();
      setRows(res.data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const anyModalOpen = open || !!preview;
    const { body } = document;
    const prev = body.style.overflow;
    if (anyModalOpen) body.style.overflow = "hidden";
    else body.style.overflow = prev || "";
    return () => { body.style.overflow = prev || ""; };
  }, [open, preview]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (open) setOpen(false);
        if (preview) setPreview(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, preview]);

  // revoke object URLs on unmount / change
  useEffect(() => {
    return () => newImages.forEach((n) => URL.revokeObjectURL(n.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalImageCount = existingImages.length + newImages.length;

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setExistingImages([]);
    setNewImages([]);
    setLinks([]);
    setError("");
    setOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm({ title: row.title, content: row.content, status: row.status, author: row.author || DEFAULT_AUTHOR });
    setExistingImages(row.images || []);
    setNewImages([]);
    setLinks((row.links || []).map((l) => ({ label: l.label || "", url: l.url || "" })));
    setError("");
    setOpen(true);
  }

  function handlePickImages(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_IMAGES - totalImageCount;
    if (room <= 0) {
      setError(`You can only have up to ${MAX_IMAGES} images per blog.`);
      return;
    }
    const accepted = files.slice(0, room).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setNewImages((prev) => [...prev, ...accepted]);
    if (files.length > room) {
      setError(`Only ${room} more image(s) could be added (max ${MAX_IMAGES} total).`);
    }
  }

  function removeExistingImage(fileName) {
    setExistingImages((prev) => prev.filter((i) => i.fileName !== fileName));
  }
  function removeNewImage(previewUrl) {
    setNewImages((prev) => {
      const target = prev.find((n) => n.previewUrl === previewUrl);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((n) => n.previewUrl !== previewUrl);
    });
  }

  function addLinkRow() {
    setLinks((prev) => [...prev, { label: "", url: "" }]);
  }
  function updateLink(idx, field, value) {
    setLinks((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  }
  function removeLink(idx) {
    setLinks((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    try {
      setError("");
      if (!form.title?.trim() || !form.content?.trim()) {
        setError("Title and Content are required");
        return;
      }
      const cleanLinks = links
        .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
        .filter((l) => l.url);

      setSaving(true);
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("content", form.content);
      fd.append("status", form.status);
      fd.append("author", form.author?.trim() || DEFAULT_AUTHOR);
      fd.append("links", JSON.stringify(cleanLinks));
      newImages.forEach((n) => fd.append("images", n.file));

      if (editing) {
        fd.append("existingImages", JSON.stringify(existingImages.map((i) => i.fileName)));
        await api.update(editing._id, fd);
      } else {
        await api.create(fd);
      }

      setOpen(false);
      await load();
    } catch (e) {
      setError(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Delete blog: ${row.title}?`)) return;
    try {
      await api.remove(row._id);
      await load();
    } catch (e) {
      alert(e.message || "Failed to delete");
    }
  }

  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "");

  return (
    <Page>
      <Row>
        <Title>Blog — Posts</Title>
        <Button onClick={openCreate}>+ Add New</Button>
      </Row>

      <Card>
        <Scroll>
          <Table>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Title</th>
                <th>Author</th>
                <th>Posted</th>
                <th>Status</th>
                <th>Content</th>
                <th style={{ width: 80 }}>Images</th>
                <th style={{ width: 80 }}>Links</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ padding: 20 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={9} style={{ padding: 20 }}>No blog posts yet.</td></tr>}
              {!loading && rows.map((r, idx) => (
                <tr key={r._id}>
                  <td>{idx + 1}</td>
                  <td>{r.title}</td>
                  <td>{r.author || DEFAULT_AUTHOR}</td>
                  <td>{fmt(r.postedAt)}</td>
                  <td><Badge $status={r.status}>{r.status}</Badge></td>
                  <td>
                    <ContentCell onClick={() => setPreview(r)} title="Click to preview">
                      {r.content || ""}
                    </ContentCell>
                  </td>
                  <td>{(r.images || []).length}</td>
                  <td>{(r.links || []).length}</td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button onClick={() => openEdit(r)}>Edit</Button>
                      <Button style={{ background: "#b91c1c" }} onClick={() => handleDelete(r)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroll>
      </Card>

      {/* Create / Edit modal */}
      {open && (
        <ModalWrap onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }} role="dialog" aria-modal="true" aria-labelledby="blog-modal-title">
          <ModalCard>
            <Row style={{ padding: "16px 16px 0" }}>
              <h3 id="blog-modal-title" style={{ margin: 0 }}>{editing ? "Edit Blog Post" : "Create Blog Post"}</h3>
            </Row>

            <ModalBody>
              <Field><span>Blog Title *</span>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. 5 Ways AI Speeds Up Site Inspections" />
              </Field>

              <Field><span>Author</span>
                <Input
                  value={form.author}
                  onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                  placeholder={DEFAULT_AUTHOR}
                  autoComplete="off"
                  name="blog-author"
                />
              </Field>

              <Field><span>Content *</span>
                <Textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} placeholder="Write the blog post..." />
              </Field>

              <Field>
                <span>Images ({totalImageCount}/{MAX_IMAGES}) — optional, up to {MAX_IMAGES}</span>
                <ImageGrid>
                  {existingImages.map((img) => (
                    <ImageTile key={img.fileName}>
                      <img src={imgSrc(img.fileName)} alt={img.originalName} />
                      <RemoveBtn type="button" onClick={() => removeExistingImage(img.fileName)} aria-label="Remove image">✕</RemoveBtn>
                    </ImageTile>
                  ))}
                  {newImages.map((n) => (
                    <ImageTile key={n.previewUrl}>
                      <img src={n.previewUrl} alt="" />
                      <RemoveBtn type="button" onClick={() => removeNewImage(n.previewUrl)} aria-label="Remove image">✕</RemoveBtn>
                    </ImageTile>
                  ))}
                  {totalImageCount < MAX_IMAGES && (
                    <AddTile>
                      +
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        multiple
                        onChange={handlePickImages}
                      />
                    </AddTile>
                  )}
                </ImageGrid>
                <HelperText>JPG, PNG, WEBP or GIF. Max 5MB each.</HelperText>
              </Field>

              <Field>
                <span>Links (optional)</span>
                <div style={{ display: "grid", gap: 8 }}>
                  {links.map((l, idx) => (
                    <LinkRow key={idx}>
                      <Input placeholder="Label (optional)" value={l.label} onChange={(e) => updateLink(idx, "label", e.target.value)} />
                      <Input placeholder="https://..." value={l.url} onChange={(e) => updateLink(idx, "url", e.target.value)} />
                      <IconBtn type="button" onClick={() => removeLink(idx)}>Remove</IconBtn>
                    </LinkRow>
                  ))}
                  <GhostBtn type="button" onClick={addLinkRow}>+ Add link</GhostBtn>
                </div>
              </Field>

              <Field>
                <span>Status</span>
                <select
                  style={{ padding: "10px 12px", borderRadius: 10, border: `1px solid ${theme.border}`, background: "#0c1626", color: theme.text }}
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </Field>

              {error && <div style={{ color: "#ffb4a6" }}>{error}</div>}
            </ModalBody>

            <Actions style={{ padding: "0 16px 16px", marginTop: "8px" }}>
              <Button onClick={() => setOpen(false)} style={{ background: "#6b7280" }}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : (editing ? "Save Changes" : "Create")}</Button>
            </Actions>
          </ModalCard>
        </ModalWrap>
      )}

      {/* Preview modal (read-only) */}
      {preview && (
        <ModalWrap onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }} role="dialog" aria-modal="true" aria-labelledby="blog-preview-title">
          <ModalCard>
            <Row style={{ padding: "16px 16px 0" }}>
              <h3 id="blog-preview-title" style={{ margin: 0 }}>Preview — {preview.title}</h3>
            </Row>

            <ModalBody>
              <HelperText>By {preview.author || DEFAULT_AUTHOR} • {fmt(preview.postedAt)} • {preview.status}</HelperText>

              {Array.isArray(preview.images) && preview.images.length > 0 && (
                <ImageGrid>
                  {preview.images.map((img) => (
                    <ImageTile key={img.fileName}>
                      <img src={imgSrc(img.fileName)} alt={img.originalName} />
                    </ImageTile>
                  ))}
                </ImageGrid>
              )}

              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{preview.content}</div>

              <Hr />
              <div>
                <strong>Links: </strong>
                {Array.isArray(preview.links) && preview.links.length > 0 ? (
                  <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
                    {preview.links.map((l, i) => (
                      <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: "#93c5fd" }}>
                        {l.label ? `${l.label} — ${l.url}` : l.url}
                      </a>
                    ))}
                  </div>
                ) : "—"}
              </div>
            </ModalBody>

            <Actions style={{ padding: "0 16px 16px" }}>
              <Button onClick={() => setPreview(null)} style={{ background: "#6b7280" }}>Close</Button>
            </Actions>
          </ModalCard>
        </ModalWrap>
      )}
    </Page>
  );
}
